/**
 * Canonical answer types, and the legacy stored values that alias onto them.
 *
 * The Question Library list printed whatever string the API returned while the
 * editor offered a fixed dropdown, so a legacy value such as "Multiple Choice"
 * showed in the list but left Answer Type blank in the editor — the `v-select`
 * renders nothing for a value that is not one of its items, and its `required`
 * rule still passed because the value was truthy.
 *
 * One registry keeps the two surfaces in agreement. Mirrors
 * `common/questionnaire/answer_types.py` on the backend.
 */

/** The answer types the authoring UI offers, in dropdown order. */
export const ANSWER_TYPES = Object.freeze([
  "Yes/No",
  "Text",
  "Number",
  "Multi-select",
  "Single-select",
  "Date",
  "Percentage",
  "Currency ($)",
  "Phone Number",
  "File upload",
]);

/**
 * Stored spelling (lowercased) -> canonical label.
 *
 * "Multiple Choice" is the seed-era spelling still stored on 52 genuine choice
 * questions. It is aliased for display, not converted: those rows keep their
 * stored value until an admin saves the question.
 */
const ANSWER_TYPE_ALIASES = Object.freeze({
  "yes/no": "Yes/No",
  "yes-no": "Yes/No",
  boolean: "Yes/No",
  text: "Text",
  number: "Number",
  "multi-select": "Multi-select",
  multiselect: "Multi-select",
  "multiple choice": "Multi-select",
  "single-select": "Single-select",
  "single select": "Single-select",
  singleselect: "Single-select",
  date: "Date",
  percentage: "Percentage",
  "currency ($)": "Currency ($)",
  currency: "Currency ($)",
  "phone number": "Phone Number",
  phone: "Phone Number",
  phonenumber: "Phone Number",
  "file upload": "File upload",
});

/**
 * The canonical label for a stored answer type, or null if unrecognized.
 *
 * null means "cannot be represented in the dropdown", which callers surface as
 * a field error rather than silently substituting a default.
 */
export function normalizeAnswerType(value) {
  if (typeof value !== "string") return null;
  return ANSWER_TYPE_ALIASES[value.trim().toLowerCase()] || null;
}

export function isSupportedAnswerType(value) {
  return normalizeAnswerType(value) !== null;
}

/**
 * Dropdown items that can always represent `storedValue`.
 *
 * An unrecognized stored value is appended rather than dropped: the editor
 * showing a blank field is what hid this defect, and silently replacing the
 * value on the next save would destroy it.
 */
export function answerTypeSelectItems(storedValue) {
  const normalized = normalizeAnswerType(storedValue);
  if (normalized || !storedValue) return [...ANSWER_TYPES];
  return [...ANSWER_TYPES, storedValue];
}

/** Field-level message for an answer type, or null when it is acceptable. */
export function answerTypeError(value) {
  if (typeof value !== "string" || !value.trim()) return "Answer type is required.";
  if (!isSupportedAnswerType(value)) {
    return `Unsupported answer type "${value.trim()}". Choose one of: ${ANSWER_TYPES.join(", ")}.`;
  }
  return null;
}

/**
 * Field-level message for a category, or null when it is acceptable.
 *
 * Category is free text drawn from the seed catalog and screen titles, so this
 * checks only that a question carries one. There is no closed list to validate
 * against and inventing one would reject valid rows.
 */
export function categoryError(value) {
  if (typeof value !== "string" || !value.trim()) return "Category is required.";
  return null;
}

/** Field-level errors for the two fields every canonical question must carry. */
export function requiredFieldErrors({ answerType, category }) {
  const errors = {};
  const answerTypeMessage = answerTypeError(answerType);
  if (answerTypeMessage) errors.answerType = answerTypeMessage;
  const categoryMessage = categoryError(category);
  if (categoryMessage) errors.category = categoryMessage;
  return errors;
}
